import * as React from 'react';
import { Edit3, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AiConfidenceChip } from '@/components/ui/ai-confidence-chip';
import { ProvenanceChip, type Origin } from '@/components/ui/provenance-chip';
import { CitationChip, type CitationChipData } from '@/components/ui/citation-chip';
import { IvvGateChip } from '@/components/ui/ivv-gate-chip';
import { KpiSourcePill } from '@/components/ui/kpi-source-pill';

/**
 * BaselineSection — T-0102 / E06-F01 §1.
 *
 * Renders one of the five baseline-forming item categories on the Baseline
 * screen (milestones / payment terms / KPIs / SLAs / deliverables). Each
 * section is a Card with a title, an item count, and a list of rows. Each
 * row carries:
 *
 *   - The item title + a short value (date / amount / target / threshold)
 *   - ProvenanceChip (always — every register row carries an origin)
 *   - AI confidence chip (when origin is AI-derived)
 *   - Citation chip (when the item carries a primary citation)
 *   - KpiSourcePill (when the item carries a $ value; the "every metric cites
 *     its origin" rule per README §6 / §8 pattern #4)
 *   - Edit button → fires `data-open-override` for the T-0113 modal
 *
 * The design contract is the `screens-modules-tabs.js → contractBaseline`
 * Milestones / Payments / KPIs / SLAs tables (lines 333-406) — same per-row
 * grammar, but rendered as a card-list rather than a wide table so the
 * sections compose under the SOW version pinner without the screen
 * exploding into 4 separate tables.
 *
 * Per the AI rule (CLAUDE.md hard rule #7): the LLM never produces numbers.
 * Value formatting (currency, date, percentage) is done in pure code at
 * render time — the BaselineSection's job is to display whatever string
 * the caller passes via `valueLabel`.
 */

export interface BaselineItemSource {
  /** Free-form provenance attribution, e.g. "SOW v2 §3.6 · CO-04". Shown
   *  in a KpiSourcePill when present. */
  text: string;
}

export interface BaselineItem {
  /** Stable id for keys + the data-open-override target. */
  id: string;
  /** Short identifier rendered in mono, e.g. "M-01". Optional. */
  code?: string;
  /** Human-readable label of the item. */
  title: string;
  /** Optional value chip — date / amount / threshold / target. */
  valueLabel?: string;
  /** UI provenance origin (translated from backend via registers.backendOriginToProvenance). */
  origin: Origin;
  /** AI confidence (0..1) — when present, renders the confidence chip. */
  aiConfidence?: number | null;
  /** Optional primary citation to render. */
  citation?: CitationChipData | null;
  /** Optional source pill rendered under the row — used for $ values. */
  source?: BaselineItemSource | null;
  /** Free-form review state, e.g. "Approved", "Pending", "Revise & resubmit". */
  reviewState?: string | null;
  /** True for rows that support per-row actions (e.g. the EDL move menu). */
  movable?: boolean;
  /** Register type backing this row — enables the override host to fetch it. */
  registerType?: string;
  /** A milestone the vendor contract names as an IV&V gate (E19 / T-0218). */
  ivvGate?: boolean;
  /**
   * When set, the row's Edit opens this instead of the T-0113 override
   * modal — the EDL milestone rows open the deliverable drawer's edit form.
   */
  onEdit?: () => void;
}

export interface BaselineSectionProps {
  /** Section title, e.g. "Milestones · 4". */
  title: string;
  description?: string;
  icon: LucideIcon;
  /** Tailwind class for the left-rule accent (defaults to ink-200). */
  accent?: string;
  items: ReadonlyArray<BaselineItem>;
  /** Empty-state message when items is empty. */
  emptyMessage?: string;
  /** When set, every row's Edit affordance opens a citation modal first. */
  onCitationOpen?: (citationId: string) => void;
  /** Optional per-row action node (e.g. the EDL "Move to" menu). */
  renderActions?: (item: BaselineItem) => React.ReactNode;
}

export function BaselineSection({
  title,
  description,
  icon: Icon,
  accent = 'border-l-ink-200',
  items,
  emptyMessage,
  onCitationOpen,
  renderActions,
}: BaselineSectionProps) {
  return (
    <Card className={cn('overflow-hidden border-l-[3px]', accent)}>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3">
            <span
              className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-card bg-paper-100 text-ink-700"
              aria-hidden="true"
            >
              <Icon className="h-3.5 w-3.5" />
            </span>
            <div>
              <CardTitle className="text-h3 font-semibold text-ink-900">{title}</CardTitle>
              {description ? <p className="mt-1 text-meta text-ink-500">{description}</p> : null}
            </div>
          </div>
          <span className="rounded-chip bg-ink-100 px-2 py-0.5 text-meta text-ink-700">
            {items.length}
          </span>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {items.length === 0 ? (
          <p className="text-body-sm text-ink-500">
            {emptyMessage ?? 'No items in this section yet.'}
          </p>
        ) : (
          <ul role="list" className="divide-y divide-ink-100">
            {items.map((item) => (
              <BaselineRow
                key={item.id}
                item={item}
                onCitationOpen={onCitationOpen}
                actions={renderActions?.(item)}
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function BaselineRow({
  item,
  onCitationOpen,
  actions,
}: {
  item: BaselineItem;
  onCitationOpen?: (citationId: string) => void;
  actions?: React.ReactNode;
}) {
  const showConfidence =
    typeof item.aiConfidence === 'number' &&
    Number.isFinite(item.aiConfidence) &&
    (item.origin === 'ai_proposed' ||
      item.origin === 'ai_confirmed' ||
      item.origin === 'consultant_edited');

  return (
    <li className="py-3">
      <div className="grid grid-cols-[1fr_auto] gap-3 items-start">
        <div className="min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-baseline gap-2">
            {item.code ? (
              <span className="font-mono text-mono-sm text-ink-500">{item.code}</span>
            ) : null}
            <span className="text-body-sm font-medium text-ink-900">{item.title}</span>
            {item.ivvGate ? <IvvGateChip /> : null}
            {item.valueLabel ? (
              <span className="rounded-chip border border-ink-100 bg-paper-50 px-2 py-0.5 font-mono text-mono-sm text-ink-700">
                {item.valueLabel}
              </span>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <ProvenanceChip origin={item.origin} />
            {showConfidence ? <AiConfidenceChip value={item.aiConfidence!} /> : null}
            {item.citation ? <CitationChip cite={item.citation} onOpen={onCitationOpen} /> : null}
            {item.reviewState ? (
              <span className="rounded-chip border border-ink-100 bg-paper-0 px-2 py-0.5 text-meta text-ink-700">
                {item.reviewState}
              </span>
            ) : null}
          </div>
          {item.source ? <KpiSourcePill source={item.source.text} className="mt-1" /> : null}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {actions}
          <Button
            type="button"
            variant="outline"
            size="sm"
            {...(item.onEdit
              ? { onClick: item.onEdit }
              : { 'data-open-override': item.id, 'data-register-type': item.registerType })}
            aria-label={`Edit ${item.title}`}
          >
            <Edit3 className="h-3.5 w-3.5" aria-hidden="true" />
            Edit
          </Button>
        </div>
      </div>
    </li>
  );
}
